import React, { useRef } from "react"
import * as Yup from 'yup';
import { Forms } from "../../common/components/CmForms";
import { DeleteButton } from "../../common/components/DeleteButton";
import { FormNumberInput } from "../../common/components/NumberInput";
import OpenModalButton from "../../common/components/openModalButton";
import StringsHelper from "../../common/stringsHelper";
import { InterventoRowDto, UnitType } from "../../generated";
import { GetUnitDescription, InterventoRowTypeSelector } from "./InterventoRowTypeSelector";
import { VatPercentageSelector } from "./VatPercentageSelector";
interface InterventiDetailExtended extends InterventoRowDto {
    totalAmount: number;
}
const { FormText, Form, Submit, } = Forms<InterventiDetailExtended>();

const validationSchema = Yup.object<Partial<InterventiDetailExtended>>({
    type: Yup
        .number()
        .required("Campo obbligatorio"),
    description: Yup
        .string()
        .required("Campo obbligatorio"),
    vat: Yup
        .number()
        .required("Campo obbligatorio"),
    amount: Yup
        .number()
        .required("Campo obbligatorio"),
})

const calculateTotalAmount = (neat: number, vatPercentage: number, discount: number, qty: number) => {
    const basePrice = (neat - (neat * discount)) * qty;
    const totalAmount = basePrice + (basePrice * vatPercentage);
    return parseFloat(totalAmount.toFixed(2));
}
const deVatAmount = (totalAmount: number, vat: number, discount: number, qty: number) => {
    const deVatAmount = (totalAmount / (1 + vat)) / qty;
    const neat = deVatAmount / (1 - discount);
    return parseFloat(neat.toFixed(2));
}

export const AddInterventoDetailForm = (props: { item: InterventoRowDto | null, onConfirm: (row: InterventoRowDto) => void }) => {
    const { item } = props;
    return (<Form
        onSubmit={props.onConfirm}
        validationSchema={validationSchema}
        initialValues={{
            vat: item?.vat ?? 0.22,
            amount: item?.amount ?? 0,
            qty: item?.qty ?? 0,
            discount: item?.discount ?? 0,
            totalAmount: item?.amount ?? 0,
            type: item?.type ?? UnitType.Pieces,
            description: item?.description ?? ""
        } as InterventiDetailExtended}
    >
        {
            ({ set, values, errors }) =>
                <div className="row">
                    <div className="col-12">
                        <FormText
                            label="Descrizione"
                            name="description"
                        />
                        {/* <input {...register("description")} placeholder="Bill" /> */}
                    </div>
                    <div className="col-12 col-md-4">
                        <FormNumberInput
                            label="Quantità"
                            value={values.qty}
                            onChange={async qty => {
                                const totalAmount = calculateTotalAmount(values.amount, values.vat, values.discount, qty);
                                await set("qty", qty);
                                await set("totalAmount", totalAmount);
                            }}
                        />
                    </div>
                    <div className="col-12 col-md-4">
                        <InterventoRowTypeSelector
                            label="Un.misura"
                            value={values.type}
                            onChange={type => set("type", type)}
                            error={errors.type}
                        />
                    </div>
                    <div className="col-12 col-md-4">
                        <FormNumberInput
                            label="Importo Netto"
                            value={values.amount}
                            onChange={async neat => {
                                const totalAmount = calculateTotalAmount(neat, values.vat, values.discount, values.qty);
                                await set("amount", neat);
                                await set("totalAmount", totalAmount);
                            }}
                        />
                    </div>
                    <div className="col-12 col-md-4">
                        <FormNumberInput
                            label="Sconto %"
                            value={parseFloat((values.discount * 100).toFixed(2))}
                            onChange={async value => {
                                const discount = value / 100;
                                const totalAmount = calculateTotalAmount(values.amount, values.vat, discount, values.qty);
                                await set("discount", discount);
                                await set("totalAmount", totalAmount);
                            }}
                        />
                    </div>
                    <div className="col-12 col-md-4">
                        <VatPercentageSelector
                            label="IVA"
                            value={values.vat}
                            onChange={async vat => {
                                const totalAmount = calculateTotalAmount(values.amount, vat, values.discount, values.qty);
                                await set("vat", vat);
                                await set("totalAmount", totalAmount);
                            }}
                            error={errors.vat}
                        />
                    </div>
                    <div className="col-12 col-md-4 ">
                        <FormNumberInput
                            label="Importo Totale"
                            value={values.totalAmount}
                            onChange={async totalAmount => {
                                const amount = deVatAmount(totalAmount, values.vat, values.discount, values.qty);
                                await set("amount", amount);
                                await set("totalAmount", totalAmount);
                            }}
                        />
                    </div>
                    <div className="col-12">
                        <Submit>Conferma</Submit>
                    </div>
                </div>
        }
    </Form >)
}

export const InterventoDetail = (props: { details: InterventoRowDto[], onChange: (details: InterventoRowDto[]) => void }) => {
    const editModalRef = useRef<OpenModalButton>(null);
    const addModalRef = useRef<OpenModalButton>(null);
    const AddInterventoComponent = <OpenModalButton
        ref={addModalRef}
        className="btn btn-primary"
        forceReload
        header={<h3 className="bg-theme text-white">Aggiungi dettaglio intervento</h3>}
        modalContent={<AddInterventoDetailForm
            item={null}
            onConfirm={x => {
                const cloned = [...props.details, x];
                props.onChange(cloned)
                addModalRef?.current?.close();
            }} />}
        title="Aggiungi riga dettaglio intervento"
    >
        <i className="fas fa-plus" /> Aggiungi dettaglio
    </OpenModalButton>

    if (props.details.length === 0)
        return <div className="alert alert-info d-flex justify-content-between align-items-center mt-2 mb-5">
            <div> Nessun dettaglio inserito</div>
            <div>{AddInterventoComponent}</div>
        </div>

    return <>
        <table className="table table-bordered table-hover align-middle">
            <thead>
                <tr>
                    <th>Descrizione</th>
                    <th>Qta</th>
                    <th>Importo</th>
                    <th>Sconto %</th>
                    <th>Iva %</th>
                    <th>Totale</th>
                </tr>
            </thead>
            <tbody>
                {props.details.map((d, i) => (
                    <tr key={i}>
                        <td>{d.description}</td>
                        <td>{StringsHelper.toDecimalString(d.qty)} {GetUnitDescription(d.type)}</td>
                        <td>€ {StringsHelper.toDecimalString(d.amount)}</td>
                        <td>{(d.discount * 100).toFixed(2)}%</td>
                        <td>{(d.vat * 100).toFixed()}%</td>
                        <td>€ {StringsHelper.toDecimalString(calculateTotalAmount(d.amount, d.vat, d.discount, d.qty))}</td>
                        <td>
                            <div className="w-100 d-flex justify-content-end btn-group">
                                <OpenModalButton
                                    ref={editModalRef}
                                    className="btn btn-primary"
                                    header={<h3 className="bg-theme text-white">Modifica dettaglio intervento</h3>}
                                    modalContent={<AddInterventoDetailForm
                                        item={d}
                                        onConfirm={item => {
                                            const cloned = [...props.details.slice(0, i), item, ...props.details.slice(i + 1)]
                                            props.onChange(cloned)
                                            editModalRef?.current?.close();
                                        }} />}
                                    title="Aggiungi riga dettaglio intervento"
                                >
                                    <i className="fas fa-edit me-1" /> Modifica
                                </OpenModalButton>
                                <DeleteButton
                                    onDelete={() => {
                                        const index = props.details.indexOf(d);
                                        if (index != -1) {
                                            const cloned = [...props.details];
                                            cloned.splice(index, 1);
                                            props.onChange(cloned);
                                        }
                                    }}
                                />
                            </div>
                        </td>
                    </tr>
                ))}
            </tbody>
        </table>
        <div className="mt-2 mb-5">
            {AddInterventoComponent}
        </div>
    </>
}
